'use client'

import { useEffect, useState } from 'react';
import Image from 'next/image';

import { Base, Checkbox, Table, TableHeader, TableRow, TableCell, TableValue, TableIcon } from '@indxsearch/systm'
import styles from './SchemaLess.module.css'

// icons
import {
  String as StringIcon,
  Array as ArrayIcon,
  Number as NumberIcon,
  Sliders_vertical,
  Nested_object,
  Json,
  Save,
  Field
} from "@indxsearch/pixl";
import FeatureIcon from '../components/FeatureIcon';

export default function SchemaLess() {
  const [mounted, setMounted] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    setMounted(true);

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDark(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const ImageSrc = `/images/fieldconfiguration${isDark ? '-dark' : ''}.svg`;

  const [fields, setFields] = useState({
    title: { searchable: true, filterable: false, facetable: false, sortable: true },
    description: { searchable: true, filterable: false, facetable: false, sortable: true },
    actors: { searchable: true, filterable: true, facetable: true, sortable: false },
    genre: { searchable: false, filterable: true, facetable: true, sortable: false },
    rating: { searchable: false, filterable: true, facetable: true, sortable: true },
    releaseYear: { searchable: false, filterable: true, facetable: true, sortable: true },
  });

  const updateField = (field: keyof typeof fields, property: keyof typeof fields.title) => {
    setFields(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [property]: !prev[field][property]
      }
    }));
  };
  return (
    <div className={styles.wrapper}>
        <div className={styles.inside}>
          <Base className={styles.base}>
              <div className={styles.baseinner}>
                  <div className={styles.title}>
                      <h2>Schema-less JSON workflow</h2>
                      <p>Built-in type detection, nesting support, and simple field setup.</p>
                      <ul>
                        <li><FeatureIcon icon={<Sliders_vertical/>} label='Faceted navigation'></FeatureIcon></li>
                        <li><FeatureIcon icon={<Nested_object/>} label='Structured content'></FeatureIcon></li>
                        <li><FeatureIcon icon={<Json/>} label='JSON analysis'></FeatureIcon></li>
                        <li><FeatureIcon icon={<Save/>} label='Load/Save function'></FeatureIcon></li>
                      </ul>
                  </div>
                  <div className={styles.content}>
                    <div className={styles.tableHeader}>
                      <Field size="14px" color="currentColor" />
                      <span>Field configuration</span>
                    </div>
                    <div className={styles.tableWrapper}>
                      <Table aria-label="Field configuration settings">
                      <TableHeader>
                        <th>Field name and type</th>
                        <th>Searchable</th>
                        <th>Filterable</th>
                        <th>Facetable</th>
                        <th>Sortable</th>
                      </TableHeader>
                      <tbody>
                        <TableRow>
                          <TableCell label="Title">
                            <TableValue>String</TableValue>
                            <TableIcon><StringIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.title.searchable} onChange={() => updateField('title', 'searchable')} aria-label="Title searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.title.filterable} onChange={() => updateField('title', 'filterable')} aria-label="Title filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.title.facetable} onChange={() => updateField('title', 'facetable')} aria-label="Title facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.title.sortable} onChange={() => updateField('title', 'sortable')} aria-label="Title sortable" /></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell label="Description">
                            <TableValue>String</TableValue>
                            <TableIcon><StringIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.description.searchable} onChange={() => updateField('description', 'searchable')} aria-label="Description searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.description.filterable} onChange={() => updateField('description', 'filterable')} aria-label="Description filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.description.facetable} onChange={() => updateField('description', 'facetable')} aria-label="Description facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.description.sortable} onChange={() => updateField('description', 'sortable')} aria-label="Description sortable" /></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell label="Actors">
                            <TableValue>Array</TableValue>
                            <TableIcon><ArrayIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.actors.searchable} onChange={() => updateField('actors', 'searchable')} aria-label="Actors searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.actors.filterable} onChange={() => updateField('actors', 'filterable')} aria-label="Actors filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.actors.facetable} onChange={() => updateField('actors', 'facetable')} aria-label="Actors facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.actors.sortable} onChange={() => updateField('actors', 'sortable')} aria-label="Actors sortable" /></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell label="Genre">
                            <TableValue>Array</TableValue>
                            <TableIcon><ArrayIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.genre.searchable} onChange={() => updateField('genre', 'searchable')} aria-label="Genre searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.genre.filterable} onChange={() => updateField('genre', 'filterable')} aria-label="Genre filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.genre.facetable} onChange={() => updateField('genre', 'facetable')} aria-label="Genre facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.genre.sortable} onChange={() => updateField('genre', 'sortable')} aria-label="Genre sortable" /></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell label="Rating">
                            <TableValue>Number</TableValue>
                            <TableIcon><NumberIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.rating.searchable} onChange={() => updateField('rating', 'searchable')} aria-label="Rating searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.rating.filterable} onChange={() => updateField('rating', 'filterable')} aria-label="Rating filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.rating.facetable} onChange={() => updateField('rating', 'facetable')} aria-label="Rating facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.rating.sortable} onChange={() => updateField('rating', 'sortable')} aria-label="Rating sortable" /></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell label="ReleaseYear">
                            <TableValue>Number</TableValue>
                            <TableIcon><NumberIcon /></TableIcon>
                          </TableCell>
                          <TableCell><Checkbox checked={fields.releaseYear.searchable} onChange={() => updateField('releaseYear', 'searchable')} aria-label="Release year searchable" /></TableCell>
                          <TableCell><Checkbox checked={fields.releaseYear.filterable} onChange={() => updateField('releaseYear', 'filterable')} aria-label="Release year filterable" /></TableCell>
                          <TableCell><Checkbox checked={fields.releaseYear.facetable} onChange={() => updateField('releaseYear', 'facetable')} aria-label="Release year facetable" /></TableCell>
                          <TableCell><Checkbox checked={fields.releaseYear.sortable} onChange={() => updateField('releaseYear', 'sortable')} aria-label="Release year sortable" /></TableCell>
                        </TableRow>
                      </tbody>
                    </Table>
                    </div>
                  </div>
              </div>
          </Base>
        </div>
    </div>
  )
}